const DEFAULT_CUSTOM_STRENGTH = 65;
const quietNodeCache = new WeakMap();
const quietEdgeCache = new WeakMap();

function quietNodes(nodes) {
  const cached = quietNodeCache.get(nodes);
  if (cached) return cached;
  const normalized = nodes.map((node) => ({
    ...node,
    data: { ...node.data, contextualProminence: "normal" },
  }));
  quietNodeCache.set(nodes, normalized);
  return normalized;
}

function quietEdges(edges, showRelationshipLabels) {
  const cacheKey = showRelationshipLabels ? "shown" : "hidden";
  const cached = quietEdgeCache.get(edges)?.[cacheKey];
  if (cached) return cached;
  const normalized = edges.map((edge) => ({
    ...edge,
    data: {
      ...edge.data,
      relationshipEmphasis: "normal",
      showRelationshipLabel: showRelationshipLabels,
    },
  }));
  const entries = quietEdgeCache.get(edges) || {};
  entries[cacheKey] = normalized;
  quietEdgeCache.set(edges, entries);
  return normalized;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function normalizeDeclutterStrength(value, fallback = DEFAULT_CUSTOM_STRENGTH) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(clamp(numeric, 0, 100)) : fallback;
}

function buildAdjacency(nodes, edges) {
  const nodeIds = new Set(nodes.map((node) => String(node.id)));
  const incident = new Map([...nodeIds].map((nodeId) => [nodeId, []]));
  const neighbors = new Map([...nodeIds].map((nodeId) => [nodeId, new Set()]));
  const degree = new Map([...nodeIds].map((nodeId) => [nodeId, 0]));

  for (const edge of edges) {
    const source = String(edge.source);
    const target = String(edge.target);
    if (source === target || !nodeIds.has(source) || !nodeIds.has(target)) continue;
    incident.get(source).push(edge);
    incident.get(target).push(edge);
    neighbors.get(source).add(target);
    neighbors.get(target).add(source);
    degree.set(source, degree.get(source) + 1);
    degree.set(target, degree.get(target) + 1);
  }
  return { nodeIds, incident, neighbors, degree };
}

export function isRelationshipDense(nodes, edges) {
  return nodes.length >= 12 && (edges.length >= 24 || nodes.length >= 28);
}

export function automaticDeclutterStrength(nodes, edges) {
  if (!isRelationshipDense(nodes, edges)) return 0;
  const nodePressure = clamp((nodes.length - 10) / 46, 0, 1);
  const edgeRatio = edges.length / Math.max(1, nodes.length);
  const edgePressure = clamp((edgeRatio - 0.9) / 1.6, 0, 1);
  const crossingPressure = clamp((edges.length - 20) / 90, 0, 1);
  // Dense maps should arrive already composed, rather than waiting for a
  // selection before their hierarchy becomes visible.
  return Math.round(clamp(42 + nodePressure * 25 + edgePressure * 22 + crossingPressure * 12, 0, 90));
}

export function resolvedDeclutterStrength(nodes, edges, focusMode = "disabled", customStrength = DEFAULT_CUSTOM_STRENGTH) {
  if (focusMode === "off" || focusMode === "disabled") return 0; // "off" is backwards compatibility.
  if (focusMode === "always") return normalizeDeclutterStrength(customStrength); // Backwards compatibility.
  if (focusMode === "custom") return normalizeDeclutterStrength(customStrength);
  return automaticDeclutterStrength(nodes, edges);
}

export function getStructuralEdgeIds(nodes, edges) {
  const { nodeIds, incident, degree } = buildAdjacency(nodes, edges);
  const nodeById = new Map(nodes.map((node) => [String(node.id), node]));
  const unvisited = new Set(nodeIds);
  const structuralEdges = new Set();

  function nodeScore(nodeId) {
    const importance = nodeById.get(nodeId)?.data?.importance;
    return ((importance === "focal" || importance === "core" || nodeById.get(nodeId)?.data?.is_core) ? 10000 : 0)
      + (degree.get(nodeId) || 0) * 20;
  }

  while (unvisited.size) {
    const root = [...unvisited].sort((first, second) => nodeScore(second) - nodeScore(first))[0];
    const queue = [root];
    unvisited.delete(root);
    while (queue.length) {
      const parent = queue.shift();
      const candidates = (incident.get(parent) || [])
        .map((edge) => {
          const source = String(edge.source);
          const target = String(edge.target);
          const child = source === parent ? target : source;
          return { child, edge, score: (source === parent ? 8 : 0) + (degree.get(child) || 0) * 2 + nodeScore(child) / 10000 };
        })
        .filter(({ child }) => unvisited.has(child))
        .sort((first, second) => second.score - first.score);
      for (const candidate of candidates) {
        if (!unvisited.has(candidate.child)) continue;
        unvisited.delete(candidate.child);
        queue.push(candidate.child);
        structuralEdges.add(String(candidate.edge.id));
      }
    }
  }
  return structuralEdges;
}

export function distancesFromProminentNodes(nodes, edges) {
  const { neighbors } = buildAdjacency(nodes, edges);
  const anchors = nodes
    .filter((node) => ["focal", "core"].includes(node.data?.importance) || node.data?.is_core)
    .map((node) => String(node.id));
  const distances = new Map();
  const queue = [];
  anchors.forEach((nodeId) => {
    distances.set(nodeId, 0);
    queue.push(nodeId);
  });
  while (queue.length) {
    const current = queue.shift();
    for (const neighbor of neighbors.get(current) || []) {
      if (distances.has(neighbor)) continue;
      distances.set(neighbor, distances.get(current) + 1);
      queue.push(neighbor);
    }
  }
  return { anchors, distances, neighbors };
}

function nodeRestOpacity(distance, intensity) {
  const emphasis = 1 - ((100 - intensity) / 100) ** 2;
  const targetOpacity = distance == null
    ? 0.7
    : [1, 0.94, 0.74, 0.5, 0.34, 0.26][Math.min(distance, 5)];
  return clamp(1 - emphasis * (1 - targetOpacity), 0.26, 1);
}

function edgeRestOpacity(distance, intensity, structural, strength) {
  const emphasis = 1 - ((100 - intensity) / 100) ** 2;
  const structuralTargets = [0.94, 0.8, 0.63, 0.48, 0.36];
  const contextualTargets = [0.52, 0.3, 0.15, 0.08, 0.05];
  const targetOpacity = distance == null
    ? (structural ? 0.72 : 0.1)
    : (structural ? structuralTargets : contextualTargets)[Math.min(distance, 4)];
  let opacity = 1 - emphasis * (1 - targetOpacity);
  if (strength === "weak") opacity -= 0.08 * emphasis;
  if (strength === "strong") opacity += 0.08 * emphasis;
  return clamp(opacity, 0.04, 1);
}

export function applyContextualVisibility(
  nodes,
  edges,
  {
    focusedNodeId = null,
    focusedEdgeId = null,
    selectedNodeId = null,
    selectedEdgeId = null,
    focusMode = "disabled",
    declutterStrength = DEFAULT_CUSTOM_STRENGTH,
    showRelationshipLabels = true,
  } = {}
) {
  const intensity = resolvedDeclutterStrength(nodes, edges, focusMode, declutterStrength);
  if (intensity === 0) {
    return {
      intensity,
      nodes: quietNodes(nodes),
      edges: quietEdges(edges, showRelationshipLabels),
    };
  }

  const { distances, neighbors } = distancesFromProminentNodes(nodes, edges);
  const structuralEdges = getStructuralEdgeIds(nodes, edges);
  const edgeById = new Map(edges.map((edge) => [String(edge.id), edge]));
  const activeEdgeId = String(focusedEdgeId || selectedEdgeId || "");
  const activeNodeId = String(focusedNodeId || (!activeEdgeId ? selectedNodeId : null) || "");
  const activeEdge = edgeById.get(activeEdgeId);
  const revealedNodes = new Set();
  if (activeNodeId) {
    revealedNodes.add(activeNodeId);
    for (const neighbor of neighbors.get(activeNodeId) || []) revealedNodes.add(neighbor);
  }
  if (activeEdge) {
    revealedNodes.add(String(activeEdge.source));
    revealedNodes.add(String(activeEdge.target));
  }
  const hasFocus = revealedNodes.size > 0;

  const visibleNodes = nodes.map((node) => {
    const nodeId = String(node.id);
    const restOpacity = nodeRestOpacity(distances.get(nodeId), intensity);
    const revealed = revealedNodes.has(nodeId);
    const opacity = revealed ? 1 : hasFocus ? clamp(restOpacity * (1 - intensity * 0.0065), 0.14, 0.72) : restOpacity;
    return {
      ...node,
      data: { ...node.data, contextualProminence: revealed ? "focused" : "context" },
      style: { ...node.style, opacity, transition: "opacity 160ms ease" },
    };
  });

  const visibleEdges = edges.map((edge) => {
    const edgeId = String(edge.id);
    const source = String(edge.source);
    const target = String(edge.target);
    const structural = structuralEdges.has(edgeId);
    const focused = edgeId === activeEdgeId || source === activeNodeId || target === activeNodeId;
    const endpointDistances = [distances.get(source), distances.get(target)].filter(Number.isFinite);
    const distance = endpointDistances.length ? Math.min(...endpointDistances) : null;
    const strength = edge.style?.strength || edge.data?.strength || "inherit";
    const restOpacity = edgeRestOpacity(distance, intensity, structural, strength);
    const contextualOpacity = focused ? 1 : hasFocus
      ? clamp(restOpacity * (structural ? 1 - intensity * 0.006 : 1 - intensity * 0.008), 0.025, 0.42)
      : restOpacity;
    const opacity = contextualOpacity * (Number(edge.data?.themeEdgeOpacity) || 1);
    const showAtRest = restOpacity >= 0.69 && (structural || distance === 0);
    return {
      ...edge,
      data: {
        ...edge.data,
        relationshipEmphasis: focused ? "focused" : structural ? "structural" : "context",
        contextualDistance: distance,
        showRelationshipLabel: showRelationshipLabels && (focused || (!hasFocus && showAtRest)),
      },
      style: { ...edge.style, opacity },
    };
  });
  return { intensity, nodes: visibleNodes, edges: visibleEdges };
}

export function applyRelationshipVisibility(nodes, edges, options = {}) {
  return applyContextualVisibility(nodes, edges, options).edges;
}
